// src/SeedTeams.js
import React, { useState } from "react";
import { supabase } from "./supabaseClient";
import toast from "react-hot-toast";
import teamsData from "./teamsData.json"; // Imported from your converted CSV/JSON file

const SeedTeams = () => {
  const [status, setStatus] = useState("Ready to update");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdateStats = async () => {
    setIsUpdating(true);
    setStatus("Updating team metrics, prices, and buy/sell values...");

    try {
      const today = new Date().toISOString().split("T")[0];

      // 1. Fetch current team data from Supabase to preserve history, shares, and schedules
      const { data: existingTeams, error: fetchError } = await supabase
        .from("teams")
        .select("id, price_history");

      if (fetchError) throw fetchError;

      // Map existing teams into a lookup dictionary for quick access
      const existingMap = {};
      (existingTeams || []).forEach((t) => {
        existingMap[t.id] = t;
      });

      let updatedCount = 0;

      // 2. Loop through and update each team
      for (const team of teamsData) {
        const calculatedPrice = 10.0 + (4 * team.fpi) + (5 * team.projW) + (1 * team.playoffPct);
        const finalPrice = Math.max(Number((team.price || calculatedPrice).toFixed(2)), 1.00);

        // Set buy and sell prices (matching finalPrice, or adjust if you use a spread)
        const buyPrice = finalPrice;
        const sellPrice = finalPrice;

        const currentTeamRecord = existingMap[team.name];
        if (!currentTeamRecord) {
          console.warn(`Could not find team in database matching ID: "${team.name}"`);
          continue;
        }

        // Handle price history: append today's price or update today's entry if it already exists
        let updatedHistory = currentTeamRecord?.price_history || [];
        const todayIndex = updatedHistory.findIndex((h) => h.date === today);
        
        if (todayIndex >= 0) {
          updatedHistory[todayIndex].price = finalPrice;
        } else {
          updatedHistory.push({ date: today, price: finalPrice });
        }

        // 3. Perform targeted update including buyprice and sellprice
        const { error: updateError } = await supabase
          .from("teams")
          .update({
            price: finalPrice,
            buy_price: buyPrice,
            sell_price: sellPrice,
            conference: team.conference,
            fpi: team.fpi,
            proj_w: team.projW,
            playoff_pct: team.playoffPct,
            price_history: updatedHistory,
            strength_rating: finalPrice,
          })
          .eq("id", team.name);

        if (updateError) {
          console.error(`Error updating ${team.name}:`, updateError);
        } else {
          updatedCount++;
        }
      }

      setStatus(`Successfully updated ${updatedCount} teams!`);
      toast.success(`Successfully updated stats, prices, and buy/sell values for ${updatedCount} teams!`);
    } catch (error) {
      console.error("Error updating team stats:", error);
      setStatus("Update failed. Check console.");
      toast.error(`Update failed: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div>
      <p style={{ margin: "0 0 10px 0", color: "#4b5563" }}>
        Status: <strong>{status}</strong>
      </p>

      <button
        onClick={handleUpdateStats}
        disabled={isUpdating}
        style={{
          padding: "8px 16px",
          backgroundColor: isUpdating ? "#9ca3af" : "#2563eb",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: isUpdating ? "not-allowed" : "pointer",
          fontWeight: "bold",
        }}
      >
        {isUpdating ? "Updating..." : "Update Stats & Prices"}
      </button>
    </div>
  );
};

export default SeedTeams;